import { useEffect, useRef, useState, useMemo } from 'react';
import { Box, Typography, useTheme, Divider } from '@mui/material';
import CcfAgentCopilotRunningSummaryStyles from './ccf-agent-copilot-running-summary.styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useDispatch, useSelector } from 'react-redux';
import { getConversationAssistanceAdaptiveCards } from './ccf-copilot-chat/+state/ccf-live-copilot.slice';
import { CcfCard, useTranslator } from '@nice-ccf/ui-controls';
import { getActiveContactID } from '../../ccf-inbox/ccf-inbox.slice';
import { AdaptiveCard } from 'adaptivecards-react';
import * as ACData from 'adaptivecards-templating';
import {
  ActionMetaData,
  CardData,
  CcfAgentCopilotContainerFunctions,
} from './ccf-live-copilot-helper';

type CcfAgentCopilotRunningSummaryProps = {
  summary: string;
};

/**
 *custom hook to get the width of components
 * @returns -refrence, component
 * @example - `useContainerWidthObserver()`
 */
export const useContainerWidthObserver = (
  containerRef: React.RefObject<HTMLDivElement>
) => {
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    const resizeObserver = new ResizeObserver((entries) => {
      setContainerWidth(entries[0].contentRect.width);
    });
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }
    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return { containerWidth };
};

/**
 * Component that displays copilot sticky running summary
 * @param summary - summary text to display
 * @returns
 * @example
 */
const CcfAgentCopilotRunningSummary = ({
  summary,
}: CcfAgentCopilotRunningSummaryProps) => {
  const [translate] = useTranslator();
  const theme = useTheme();
  const lineHeight = 15;
  const fontSize = 12;
  const letterSpacing = 0.15;
  const runningSummaryStyles = CcfAgentCopilotRunningSummaryStyles(
    theme,
    lineHeight,
    fontSize
  );
  const containerRef = useRef<HTMLInputElement>(null);
  const [isExpanded, setIsExpanded] = useState(true);
  const { containerWidth } = useContainerWidthObserver(containerRef);
  const activeContactId = useSelector(getActiveContactID);
  const hostConfig = {
    fontFamily: theme.typography.fontFamily,
    separator: {
      lineThickness: 1,
      lineColor: theme?.palette?.background?.noteInput,
    },
    supportsInteractivity: true,
    containerStyles: {
      default: {
        foregroundColors: {
          default: {
            default: theme?.palette?.secondary?.main,
            subtle: theme?.palette?.secondary?.main,
          },
          dark: {
            default: theme?.palette?.text?.contrastText,
          },
        },
      },
    },
  };
  const context: ACData.IEvaluationContext = {
    $root: {},
  };
  const existingConversationAssistanceAdaptiveCards = useSelector(
    getConversationAssistanceAdaptiveCards(activeContactId || '')
  );
  const dispatch = useDispatch();

  const SUMMARY_MIN_LINES = 2;
  const maxCharsBeforeExpand = useMemo(
    () =>
      2 *
      SUMMARY_MIN_LINES *
      Math.round(containerWidth / (fontSize + letterSpacing)),
    [containerWidth]
  );

  /**
   * Toggle function to change isExpanded state
   * @returns
   * @example
   */
  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  // const customerQuestionsAndConcerns = useSelector(
  //   getConversationAssistance(activeContactId || '')
  // );

  // const customerQuestionsAndConcerns = [
  //   {
  //     customerConcern: 'Oven is acting up today',
  //     customerQuestionOrStatement:
  //       'Hi, this is Julie. Thanks for reaching out to OneAppliance. How can I help you today?',
  //     isConcernResolvedByAgent: 'No',
  //     CSVStepsFollowedByAgent: '',
  //   },
  //   {
  //     customerConcern: "Oven door is locked and won't open",
  //     customerQuestionOrStatement:
  //       'Hi, this is Julie. Thanks for reaching out to OneAppliance. How can I help you today?',
  //     isConcernResolvedByAgent: 'Yes',
  //     CSVStepsFollowedByAgent: '',
  //   },
  //   {
  //     customerConcern: "Oven door is locked and won't open 2",
  //     customerQuestionOrStatement:
  //       'Hi, this is Julie. Thanks for reaching out to OneAppliance. How can I help you today?',
  //     isConcernResolvedByAgent: 'No',
  //     CSVStepsFollowedByAgent: '',
  //   },
  //   {
  //     customerConcern: "Oven door is locked and won't open 3",
  //     customerQuestionOrStatement:
  //       'Hi, this is Julie. Thanks for reaching out to OneAppliance. How can I help you today?',
  //     isConcernResolvedByAgent: 'Yes',
  //     CSVStepsFollowedByAgent: '',
  //   },
  // ]

  //  /**
  //  * Function is helps in calling function when button is clicked on popup
  //  * @param item -String
  //  * @returns Function
  //  * @example onPopOverItemSelections('fetchingInteractions')
  //  */
  //  const onPopOverItemSelection = (item: PopOverMenuItem) => () => {
  //   switch (item.label) {
  //     case 'Search Topic':
  //       return dispatch(setAgentSearchTopic(customerQuestionsAndConcerns[0]?.customerConcern ?? ''));
  //     case 'Remove Topic':
  //       return dispatch(removeConversationAssistanceItem({caseId:`${activeContactId} || ''`, customerConcern: customerQuestionsAndConcerns[0]?.customerConcern ?? ''}))
  //     default:
  //       return console.log('Please select correct option');
  //   }
  // };

  const dropdownOptions = {
    menuItems: [
      {
        items: [
          {
            label: translate('copilotInsight'),
            closeOnSelection: true,
          },
          {
            label: translate('removeTopic'),
            closeOnSelection: true,
          },
        ],
      },
    ],
  };
  /**
   * Component that displays expand collapse container
   * @returns
   * @example
   */
  const ExpandContainer = () => (
    <Box onClick={toggleExpanded}>
      {isExpanded && existingConversationAssistanceAdaptiveCards.length > 0 ? <ExpandLessIcon /> : <ExpandMoreIcon />}
    </Box>
  );

  const isSmView = false;
  /**
   * Function to handle on click event for actions
   * @param action - ActionMetaData
   * @param cardItem - CardData
   * @param cardId - number
   * @example handleOnClick(action, cardItem, cardId)
   */
  const handleOnClick = async (
    action: ActionMetaData,
    cardItem: CardData,
    cardId: number
  ) => {
    const caseId = activeContactId || '';
    await CcfAgentCopilotContainerFunctions.handleClickActions(
      { action, cardItem, caseId, isSmView, cardId },
      CcfAgentCopilotContainerFunctions.handleIconChange,
      handleExpandImageClick,
      dispatch,
      ''
    );
  };

  /**
   * Function to handle on click event for expand image
   * @param imgUrl - string
   * @param imgAltText - string
   * @example handleExpandImageClick(imgUrl, imgAltText)
   */
  const handleExpandImageClick = (imgUrl: string, imgAltText: string) => {
    console.log('test');
  };

  if (!summary) return <></>;

  return (
      <Box
        sx={runningSummaryStyles.runningSummaryContainer}
        data-testid="acp-running-summary-container"
        id="acp-running-summary-container"
      >
        <Box
          ref={containerRef}
          sx={runningSummaryStyles.summaryContainer}
        >
          <Typography
            variant="h5"
            sx={  runningSummaryStyles.summaryText}
            //   (isExpanded && existingConversationAssistanceAdaptiveCards.length > 0)
            //     ? [
            //         runningSummaryStyles.summaryText,
            //         runningSummaryStyles.expandedSummaryText,
            //       ]
            //     : runningSummaryStyles.summaryText
            // }
          >
            {summary}
          </Typography>
          <ExpandContainer />
        </Box>
        {isExpanded && existingConversationAssistanceAdaptiveCards.length > 0 && <Divider style={{ height: '1px', width: '100%', borderBottomWidth: 3 }} />}
        {isExpanded && existingConversationAssistanceAdaptiveCards.length > 0 && (
        <Box sx={runningSummaryStyles.ConversationAssistanceContainer}>
              <Typography variant="h5" sx={runningSummaryStyles.topicsHeading}>
              {translate('conversationTopics')}
              </Typography>
              {existingConversationAssistanceAdaptiveCards.length > 0 && (
                <Box sx={runningSummaryStyles.conversationAssistanceBox}>
                  <CcfCard
                    sx={runningSummaryStyles.copilotCard}
                    data-testid="list-item"
                  >
                    <div ref={containerRef}>
                      {existingConversationAssistanceAdaptiveCards.map(
                        (card, cardId) => {
                          let updatedCopilotCard = { ...card.content };
                          try {
                            updatedCopilotCard = JSON.stringify(
                              card.content
                            ).replace(/adp_\w+/g, (match) => {
                              const value = translate(match);
                              return value ? value : match;
                            });
                            updatedCopilotCard = JSON.parse(updatedCopilotCard);
                          } catch (e) {
                            console.log('[error] ------->', e);
                          }
                          const template = new ACData.Template(updatedCopilotCard);
                          const copilotCard = template.expand(context);
                          return (
                            <div
                              style={runningSummaryStyles.copilotContent}
                              key={cardId}
                            >
                              <AdaptiveCard
                                key={`ad-${cardId}`}
                                style={{
                                  fontFamily: theme.typography.fontFamily,
                                }}
                                payload={copilotCard}
                                hostConfig={hostConfig}
                                onExecuteAction={(action: any) =>
                                  handleOnClick(action, copilotCard, cardId)
                                }
                              />
                            </div>
                          );
                        }
                      )}
                    </div>
                  </CcfCard>
                </Box>
              )}
            </Box>
        )}
      </Box>
  );
};

export default CcfAgentCopilotRunningSummary;